import { useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import { getMonthToDateRange } from '@/utils/date';
import { DATE_RANGE_FILTER_KEYS } from '@/utils/filterFields';
import type { LeaveReportParams, LeaveReportPeriod } from '../types/leaveReport.types';
import { toLeaveReportFilterSearch, toLeaveReportFilterValues } from '../utils/leaveReportFilters';

const route = getRouteApi('/_auth/hr-form/leave-report');

const normalizeFilterValues = (values: Record<string, string>) =>
  toLeaveReportFilterValues(toLeaveReportFilterSearch(values));

export function useLeaveReportFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const appliedValues = useMemo(() => toLeaveReportFilterValues(search), [search]);
  const defaultValues = useMemo(() => getMonthToDateRange(), []);

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    defaultValues,
    rangeKeys: DATE_RANGE_FILTER_KEYS,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...toLeaveReportFilterSearch(values), search: previous.search }),
        replace: true,
      });
    },
  });

  const { searchText, setSearchText } = useUrlSyncedSearch({
    appliedText: search.search,
    onApply: (text) => {
      void navigate({ search: (previous) => ({ ...previous, search: text }), replace: true });
    },
  });

  const period = useMemo(
    (): LeaveReportPeriod => ({
      dateFrom: search.dateFrom ?? defaultValues.dateFrom,
      dateTo: search.dateTo ?? defaultValues.dateTo,
    }),
    [search.dateFrom, search.dateTo, defaultValues],
  );

  const params = useMemo((): LeaveReportParams => ({ ...search, ...period }), [search, period]);

  return { ...filters, params, period, searchText, setSearchText };
}
